import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import AptosNormal from "../../../../utils/Customfonts/Aptos.ttf";
import AptosBold from "../../../../utils/Customfonts/Aptos-Bold.ttf";
import moment from "moment";

// ASSET PATHS
const UnitechHeader = "../img/asset_issue_header.png";
const UnitechFooter = "../img/IR_Footer.png";

// FONT FAMILY
export const customFontFamily = {
  bold: "bold",
  normal: "normal",
  boldFont: "AptosBold",
  normalFont: "AptosNormal",
};

// CONFIG  –  same shape/keys as generalIRPdf.js
export const config = {
  margin: 15,
  borderMargin: 15,

  headerimgHeight: 70,
  footerimgHeight: 0,

  pageNoHeight: 15,
  borderGap: 2,

  fontSize: 10,
  fontSizeSm: 9,
  fontSizeXs: 7.5,
  fontSizeLg: 12,
  fontSizeTitle: 15,

  cellPadding: 4,
  sectionGap: 0,

  borderFooterGap: 15,

  get contentTop() {
    return this.headerimgHeight + this.borderGap + this.borderMargin + 15;
  },
  get contentBottom() {
    return this.footerimgHeight + this.pageNoHeight + this.borderGap + this.borderMargin + 5;
  },
};

const getBaseStyles = () => ({
  fontSize: config.fontSize,
  cellPadding: config.cellPadding,
  lineColor: [0, 0, 0],
  lineWidth: 0.5,
  textColor: [0, 0, 0],
  fillColor: false,
  font: customFontFamily.normalFont,
  fontStyle: customFontFamily.normal,
  halign: "left",
  valign: "middle",
});

const getHeaderStyles = () => ({
  ...getBaseStyles(),
  font: customFontFamily.boldFont,
  fontStyle: customFontFamily.bold,
  fillColor: false,
});

const getTableMargins = () => ({
  left: config.margin,
  right: config.margin,
  top: config.contentTop,
  bottom: config.contentBottom,
});

// LAYOUT STATE
const layout = {
  doc: null,
  y: 0,
  pageWidth: 0,
  pageHeight: 0,
  contentWidth: 0,

  init(doc) {
    this.doc = doc;
    this.pageWidth = doc.internal.pageSize.getWidth();
    this.pageHeight = doc.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - config.margin * 2;
    this.y = config.contentTop;
  },
  afterTable(gap) {
    this.y = this.doc.lastAutoTable.finalY + (gap ?? config.sectionGap);
  },
  advance(delta) {
    this.y += delta;
  },
  ensureSpace(needed = 30) {
    if (this.y + needed > this.pageHeight - config.contentBottom) {
      this.doc.addPage();
      this.y = config.contentTop;
    }
  },
};

// FONT LOADER
const arrayBufferToBase64 = (buffer) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

const loadAndAddFont = async (pdfDoc, fontUrl, fontNameTTF, fontName, fontStyle) => {
  const fontResponse = await fetch(fontUrl);
  const fontBuffer = await fontResponse.arrayBuffer();
  const fontBase64 = arrayBufferToBase64(fontBuffer);
  pdfDoc.addFileToVFS(`${fontNameTTF}.ttf`, fontBase64);
  pdfDoc.addFont(`${fontNameTTF}.ttf`, fontName, fontStyle);
};

// IMAGE LOADER — converts a signature into base64 so jsPDF can embed it.
// jsPDF's addImage cannot render a bare remote URL directly; it needs actual
// image bytes (base64 data URI) or an HTMLImageElement already loaded.
const loadImageAsDataUrl = async (url) => {
  if (!url) return null;
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.log("Failed to load profile image for PDF:", url, err);
    return null; // fall back gracefully — table still renders without the image
  }
};

// in the asset rows into a { employeeId: base64DataUrl } map.
// Must happen before autoTable draws, since didDrawCell runs synchronously.
const preloadIssuerImages = async (assetRows, employeeOptions) => {
  const idsInUse = [
    ...new Set(
      assetRows
        .map((r) => r.issuedByEmployeeId?.id ?? r.issuedByEmployeeId)
        .filter((id) => id !== null && id !== undefined && id !== "")
    ),
  ];

  const imageMap = {};
  await Promise.all(
    idsInUse.map(async (id) => {
      const option = employeeOptions.find((o) => o.id === id);
      if (option?.signature) {
        imageMap[id] = await loadImageAsDataUrl(option.signature);
      }
    })
  );

  return imageMap;
};

// PAGE DECORATORS  –  same structure as addHeaderFooter in generalIRPdf.js
const addHeaderFooter = (doc) => {
  const marginX = 0;
  const width = layout.pageWidth;

  if (config.headerimgHeight > 0) {
    doc.addImage(UnitechHeader, "PNG", marginX, 0, width, config.headerimgHeight);
  }
  if (config.footerimgHeight > 0) {
    doc.addImage(
      UnitechFooter,
      "PNG",
      marginX,
      layout.pageHeight - config.footerimgHeight - 3,
      width,
      config.footerimgHeight
    );
  }
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
};

const addPageNumbers = (doc, totalPages) => {
  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page);
    addHeaderFooter(doc);

    const pageNoY = layout.pageHeight - config.footerimgHeight - config.borderFooterGap;

    doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
    doc.setFontSize(config.fontSizeSm);
    doc.setTextColor(0);
    doc.text("DYC/FMT-MR/48 Rev.: 00, Dated:24/05/2023", config.borderMargin, pageNoY);
    doc.text(
      `Page No : ${String(page).padStart(2, "0")} of ${String(totalPages).padStart(2, "0")}`,
      layout.pageWidth - config.margin - 80,
      pageNoY
    );
  }
};

// SECTION HEADING
const addSectionHeading = (title) => {
  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    body: [[title]],
    theme: "grid",
    styles: {
      ...getHeaderStyles(),
      cellPadding: { top: 4, bottom: 4, left: 5, right: 5 },
    },
    columnStyles: { 0: { cellWidth: layout.contentWidth } },
    didDrawPage: () => {
      layout.y = config.contentTop;
    },
  });
  layout.afterTable(0);
};

// EMPLOYEE DETAILS TABLE — label|value pairs, 2 per row like the sample form
const addEmployeeDetailsTable = (employee, otherData) => {
  const labelW = layout.contentWidth * 0.22;
  const valueW = layout.contentWidth - labelW;

  const rows = [
    ["Name of Employee", employee?.name || ""],
    ["Employee Id", employee?.employeesCode || ""],
    ["Designation", otherData?.hrDesignation || ""],
    ["Address", employee?.currentAddress || ""],
    ["Reporting To", otherData?.reportingManagerName || ""],
  ];

  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    body: rows.map(([label, value]) => [
      {
        content: label,
        styles: {
          ...getBaseStyles(),
          font: customFontFamily.boldFont,
          fontStyle: customFontFamily.bold,
        },
      },
      { content: value, styles: getBaseStyles() },
    ]),
    theme: "grid",
    styles: getBaseStyles(),
    columnStyles: { 0: { cellWidth: labelW }, 1: { cellWidth: valueW } },
    didDrawPage: () => {
      layout.y = config.contentTop;
    },
  });
  layout.afterTable(0);
};


// the same name + photo there — no per-row receiver data exists or is saved.
// "Issued by" column shows the photo first, then the name below it.
// "Receivers" column shows only the photo — no name text.
const addAssetDetailsTable = (assetRows, employeeOptions, issuerImageMap, employee, receiverImage) => {
  addSectionHeading("Asset Details:");

  const getId = (val) => val?.id ?? val ?? null;

  const head = [
    [
      "Sr.No.",
      "Assets Name and Model",
      "Date of Issue",
      "Return Date",
      "Issued by Name and Signature",
      "Receivers Signature",
    ],
  ];

  const body = assetRows.length
    ? assetRows.map((r, i) => [
        i + 1,
        r.assetName || "",
        r.dateOfIssue ? moment(r.dateOfIssue).format("DD/MM/YY") : "",
        r.returnDate ? moment(r.returnDate).format("DD/MM/YY") : "",
        "", // issued-by cell text is drawn manually in didDrawCell (image + name)
        "", // receiver cell shows image only — no text
      ])
    : [["—", "", "", "", "", ""]];

  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    head,
    body,
    theme: "grid",
    headStyles: getHeaderStyles(),
    styles: { ...getBaseStyles(), minCellHeight: 30 },
    columnStyles: {
      0: { cellWidth: 37, halign: "center" },
    },
    didDrawCell: (data) => {
      if (data.section !== "body") return;
      const row = assetRows[data.row.index];
      if (!row) return;

      const cellCenterX = data.cell.x + data.cell.width / 2;
      const imgW = 50;
      const imgH = 21;

      // Issued-by column: photo on top, employee name drawn below it
      if (data.column.index === 4) {
        const issuedById = getId(row.issuedByEmployeeId);
        const img = issuerImageMap[issuedById];
        const name = employeeOptions?.find((o) => o.id == issuedById)?.label || "";

        let textY;
        const imgX = cellCenterX - imgW -20;
        const imgY = data.cell.y + 4;
        if (img) {
          try {
            layout.doc.addImage(img, "PNG", imgX, imgY, imgW, imgH-5);
          } catch (_) {
            /* skip broken image */
          }
          textY = imgY + imgH ;
        } else {
          textY = imgY + imgH;
        }

        if (name) {
          layout.doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
          layout.doc.setFontSize(config.fontSize - 1);
          layout.doc.setTextColor(0);
          layout.doc.text(name, cellCenterX - 10, textY, { align: "left" });
        }
      }

      // Receiver column: image only, centered — no name text
      if (data.column.index === 5) {
        if (receiverImage) {
          const imgX = cellCenterX - imgW / 2;
          const imgY = data.cell.y + data.cell.height / 2 - imgH / 2;
          try {
            layout.doc.addImage(receiverImage, "PNG", imgX, imgY, imgW, imgH);
          } catch (_) {
            /* skip broken image */
          }
        }
      }
    },
    didDrawPage: () => {
      layout.y = config.contentTop;
    },
  });
  layout.afterTable();
};

// ACKNOWLEDGMENT — static terms with employee name injected
const addAcknowledgmentBlock = (employee) => {
  addSectionHeading("Acknowledgment by Employee:");

  const name = employee?.name || "____________________";

  const paragraph1 = `Acknowledgment by Employee: I ${name} acknowledge receipt of the above asset(s) from the Company`;

  const paragraph1b =
    "I agree to abide by the following conditions while the asset(s) is under my custody:";

  const bullets1 = [
    "The asset(s) issued to me is for official use only.",
    "I understand that the asset(s) belongs to the Company, and I am responsible for its safekeeping.",
    "The asset(s) can be requisitioned from me at any time without assigning a reason.",
    "I will maintain the asset(s) in good working condition during my possession.",
    "I will return the asset(s) upon proceeding on long leave or separation from the company.",
    "The asset(s) cannot be withheld by me pending settlement of final dues.",
    "I will not lend the asset(s) to anyone else and will prevent its misuse.",
    "I indemnify the Company against any misuse of the asset(s).",
  ];

  const paragraph2 = "I understand and agree to the additional terms of asset issuance:";

  const bullets2 = [
    "The asset is for official use only.",
    "I am fully accountable for any theft, loss, or damage to the property.",
    "Any additional software/hardware requirements must be communicated to the DYC Global Office and approved by management.",
    "Malfunctions must be reported to the DYC Global Office immediately.",
    "Repairs should not be conducted by external agencies without prior approval.",
    "The asset must be returned to the DYC Global Office upon leaving the organization or ceasing its use.",
    "I am liable to replace or reimburse the Company for any damage due to negligence.",
  ];

  const lastparagraph = `${name} have read and understood the terms and conditions outlined by DYC Global Pvt. Ltd. and hereby declare to abide by them.`;

  const lines = [
    paragraph1,
    paragraph1b,
    ...bullets1.map((b) => `\u2022  ${b}`),
    paragraph2,
    ...bullets2.map((b) => `\u2022  ${b}`),
    lastparagraph,
  ];

  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    body: [[lines.join("\n")]],
    theme: "grid",
    styles: { ...getBaseStyles(), fontSize: config.fontSizeSm, valign: "top" },
    columnStyles: { 0: { cellWidth: layout.contentWidth } },
    didDrawPage: () => {
      layout.y = config.contentTop;
    },
  });
  layout.afterTable();
};

// EMPLOYEE SIGNATURE BLOCK — uses employee.signature (pre-loaded as base64)
const addSignatureBlock = (employee, otherData, employeeSignatureImage) => {
  const doc = layout.doc;
  const boxW = layout.contentWidth * 0.45;
  const boxX = config.margin + layout.contentWidth - boxW; // right side
  const boxH = 80;

  layout.ensureSpace(boxH + 15);
  const boxY = layout.y;
  const centerX = boxX + boxW / 2;

  if (employeeSignatureImage) {
    const props = doc.getImageProperties(employeeSignatureImage);
    const maxImgW = boxW - 20;
    let imgW = maxImgW;
    let imgH = (props.height / props.width) * imgW;
    if (imgH > 40) {
      imgH = 40;
      imgW = (props.width / props.height) * imgH;
    }
    const imgX = centerX - imgW / 2;
    const imgY = boxY + 6;
    doc.addImage(employeeSignatureImage, "PNG", imgX, imgY, imgW, imgH);

    doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
    doc.setFontSize(config.fontSize);
    doc.setTextColor(0);
    doc.text(employee?.name || "", centerX, imgY + imgH + 12, { align: "center" });
  } else {
    doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
    doc.setFontSize(config.fontSize);
    doc.setTextColor(0);
    doc.text(employee?.name || "", centerX, boxY + 58, { align: "center" });
  }

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(config.fontSize);
  doc.setTextColor(0);
  doc.text("Employee Name & Signature", centerX, boxY + boxH - 5, { align: "center" });
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);

  layout.advance(boxH + 10);
};

// MAIN EXPORT
export async function generateAssetIssueRecordPDF(
  employee,
  otherData,
  assetRows = [],
  employeeOptions = [],
  setDownloadPdfloading
) {
  try {
    setDownloadPdfloading(true);

    const doc = new jsPDF({ orientation: "p", unit: "pt", format: "a4", compress: true });

    // fetches itself, so nothing can be fetched lazily during table drawing.
    const [, , issuerImageMap, employeeSignatureImage] = await Promise.all([
      loadAndAddFont(doc, AptosNormal, "Aptos", customFontFamily.normalFont, customFontFamily.normal),
      loadAndAddFont(doc, AptosBold, "Aptos-Bold", customFontFamily.boldFont, customFontFamily.bold),
      preloadIssuerImages(assetRows, employeeOptions),
      loadImageAsDataUrl(employee?.signature),
    ]);

    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);

    layout.init(doc);

    addEmployeeDetailsTable(employee, otherData);

    addAssetDetailsTable(assetRows, employeeOptions, issuerImageMap, employee, employeeSignatureImage);

    addAcknowledgmentBlock(employee);

    addSignatureBlock(employee, otherData, employeeSignatureImage);

    addPageNumbers(doc, doc.getNumberOfPages());

    doc.save(`Asset_Issue_Record_${employee?.employeesCode || employee?.id || ""}.pdf`);
  } catch (error) {
    console.error("Asset issue record PDF generation error:", error);
  } finally {
    setDownloadPdfloading(false);
  }
}